import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;  // Empty = same origin

// Global interceptor: inject Bearer token on EVERY axios request.
// This fixes all pages that were using { withCredentials: true } (cookies)
// instead of { headers: { Authorization: Bearer } }.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('kanban_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Strip withCredentials — cookies don't work cross-pod
  delete config.withCredentials;
  return config;
});

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  // Check if user is authenticated on mount using JWT token
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('kanban_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('kanban_token');
          setUser(null);
        }
      }
      setLoading(false);
      setAuthChecked(true);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('kanban_token', token);
    setUser(userData);
    return userData;
  };

  const loginWithToken = async (token, userData) => {
    localStorage.setItem('kanban_token', token);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('kanban_token');
    setUser(null);
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('kanban_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, login, loginWithToken, logout, getAuthHeader, loading, authChecked, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};
